import { useRef, useState, useEffect, useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { useFirebase, useAuthUid } from '@/App';
import { trackEvent } from '@/firebase';
import { useLocalSession } from '@hooks/useLocalSession';
import { useEntitlements } from '@hooks/useEntitlements';
import { assignMemberColour } from '@utils/colours';
import { clearRoomGameImages } from '@utils/imageStorage';
import { getStrokePath, unflattenPoints } from '@utils/strokeUtils';
import CanvasStage from '@components/CanvasStage';
import Toolbar from '@components/Toolbar';
import TopBar from '@components/TopBar';
import MembersPanel from '@components/MembersPanel';
import HostPanel from '@components/HostPanel';
import GalleryPanel from '@components/GalleryPanel';
import ReplayPanel from '@components/ReplayPanel';
import HelpPanel from '@components/HelpPanel';
import ToastStack from '@components/ToastStack';
import VoicePanel from '@components/VoicePanel';
import ArtStyleExplorer from '@components/ArtStyleExplorer';
import PaywallModal from '@components/PaywallModal';
import FocusModePill from '@components/FocusModePill';
import SelectionToolbar from '@components/SelectionToolbar';
import GameRouter from '@/games/GameRouter';
import GameModeSelector from '@/games/GameModeSelector';

export default function WhiteboardRoom() {
  const { state, dispatch } = useApp();
  const stageRef = useRef(null);
  const firebase = useFirebase();
  const authUid = useAuthUid();
  const session = useLocalSession(authUid);
  const entitlements = useEntitlements();

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [replayOpen, setReplayOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [artStylesOpen, setArtStylesOpen] = useState(false);
  const [gameSelectorOpen, setGameSelectorOpen] = useState(false);

  // Analytics tracking — game session metrics
  const gameSessionRef = useRef({
    mode: null,
    startedAt: null,
    startPlayerCount: 0,
    peakPlayerCount: 0,
    completed: false,
  });
  const { user, room, canvas, ui, game } = state;

  // Disable canvas during certain game phases
  const canvasDisabled =
    // Relay: not my turn during active phase
    (game.mode === 'relay' && game.phase === 'active' && game.currentDrawerId !== user.id)
    // Challenge: in review/voting/results phases
    || (game.mode === 'challenge' && ['review', 'voting', 'results'].includes(game.phase))
    // Squiggle: not current drawer during active phases
    || (game.mode === 'squiggle' && ['squiggling', 'transforming'].includes(game.phase) && game.currentDrawerId !== user.id)
    || (game.mode === 'squiggle' && ['pairReveal', 'gallery', 'voting', 'results'].includes(game.phase))
    // Telephone: disabled during guessing (drawing uses solo mode)
    || (game.mode === 'telephone' && game.phase === 'guessing')
    // Corpse: not current drawer during drawing
    || (game.mode === 'corpse' && game.phase === 'drawing' && game.currentDrawerId !== user.id)
    || (game.mode === 'corpse' && ['assigning', 'reveal', 'gallery'].includes(game.phase))
    // Any game in lobby/transition/finished
    || ['lobby', 'transition', 'finished'].includes(game.phase);

  // Sync host's Host Pass status to room meta whenever entitlements change.
  // This lets other clients see if the room has become a "big" workshop.
  useEffect(() => {
    if (!user.isHost || !room.code) return;
    firebase.updateHostPassStatus?.(entitlements.hasHostPass).catch(() => {});
  }, [user.isHost, room.code, entitlements.hasHostPass, firebase]);

  // Analytics — track peak player count during active game
  useEffect(() => {
    if (!gameSessionRef.current.mode) return;
    const currentCount = Object.keys(room.members || {}).length;
    if (currentCount > gameSessionRef.current.peakPlayerCount) {
      gameSessionRef.current.peakPlayerCount = currentCount;
    }
  }, [room.members]);

  // Analytics — detect game completion (vs abandoned/ended early)
  const COMPLETION_PHASES = {
    challenge: 'results',
    relay: 'finished',
    squiggle: 'gallery',
    telephone: 'reveal',
    corpse: 'gallery',
  };
  useEffect(() => {
    const session = gameSessionRef.current;
    if (!session.mode || session.completed) return;
    if (game.phase === COMPLETION_PHASES[session.mode]) {
      session.completed = true;
      const duration = session.startedAt ? Math.round((Date.now() - session.startedAt) / 1000) : 0;
      // Count engagement indicators from final game state
      const submissionCount = Object.keys(game.submissions || {}).length;
      const voteCount = Object.values(game.votes || {})
        .reduce((acc, v) => acc + Object.keys(v || {}).filter(k => k !== 'submittedAt').length, 0);
      const reactionCount = game.reactions ? Object.keys(game.reactions).length : 0;
      trackEvent('game_completed', {
        mode: session.mode,
        duration_seconds: duration,
        start_player_count: session.startPlayerCount,
        peak_player_count: session.peakPlayerCount,
        end_player_count: Object.keys(room.members || {}).length,
        submission_count: submissionCount,
        vote_count: voteCount,
        reaction_count: reactionCount,
      });
    }
  }, [game.phase, game.mode, game.submissions, game.votes, game.reactions, room.members]);

  // --- Undo ---
  const handleUndo = useCallback(() => {
    const lastId = canvas.localStrokeIds[canvas.localStrokeIds.length - 1];
    if (!lastId) return;
    dispatch({ type: 'REMOVE_STROKE', payload: lastId });
    firebase.removeStroke(lastId);
  }, [canvas.localStrokeIds, dispatch, firebase]);

  // --- Redo ---
  const handleRedo = useCallback(async () => {
    const lastRedo = canvas.redoStack[canvas.redoStack.length - 1];
    if (!lastRedo) return;
    // Re-push to RTDB
    const { id, pathData, ...data } = lastRedo;
    const strokeId = await firebase.pushStroke(data);
    if (strokeId) {
      dispatch({ type: 'REDO_STROKE', payload: { ...lastRedo, id: strokeId } });
    }
  }, [canvas.redoStack, dispatch, firebase]);

  // --- Delete selected ---
  const handleDeleteSelected = useCallback(() => {
    if (canvas.selectedStrokeIds.length === 0) return;
    canvas.selectedStrokeIds.forEach((id) => {
      dispatch({ type: 'REMOVE_STROKE', payload: id });
      firebase.removeStroke(id);
    });
    dispatch({ type: 'CLEAR_SELECTION' });
    dispatch({ type: 'ADD_TOAST', payload: { message: 'Deleted' } });
  }, [canvas.selectedStrokeIds, dispatch, firebase]);

  // --- Copy selected ---
  const handleCopy = useCallback(() => {
    if (canvas.selectedStrokeIds.length === 0) return;
    dispatch({ type: 'COPY_SELECTED' });
    dispatch({ type: 'ADD_TOAST', payload: { message: `${canvas.selectedStrokeIds.length} item(s) copied` } });
  }, [canvas.selectedStrokeIds, dispatch]);

  // --- Paste ---
  // Pastes the clipboard with a visible offset. Shifts the actual point
  // coordinates (not just Konva offset props) so the paste is clearly
  // separate from the original. Auto-selects pasted strokes for immediate
  // drag-to-position.
  const handlePaste = useCallback(async () => {
    if (canvas.clipboard.length === 0) return;

    const PASTE_SHIFT = 150; // canvas pixels — visible at any zoom

    const pastedStrokes = [];
    for (const original of canvas.clipboard) {
      const { id, pathData, ...data } = original;

      // Shift actual point coordinates so the paste is clearly offset
      let shiftedPoints = data.points;
      if (data.points && Array.isArray(data.points)) {
        shiftedPoints = [];
        for (let i = 0; i < data.points.length; i += 2) {
          shiftedPoints.push(data.points[i] + PASTE_SHIFT);
          shiftedPoints.push(data.points[i + 1] + PASTE_SHIFT);
        }
      }

      const pasteData = {
        ...data,
        points: shiftedPoints,
        // Shift shape/stamp/text positions too
        x: data.x != null ? data.x + PASTE_SHIFT : data.x,
        y: data.y != null ? data.y + PASTE_SHIFT : data.y,
        // Shift bezier points if present
        bezierPoints: data.bezierPoints
          ? data.bezierPoints.map((v, i) => v + PASTE_SHIFT)
          : data.bezierPoints,
        // Shift gradient points if present
        gradientPoints: data.gradientPoints
          ? data.gradientPoints.map((v, i) => v + PASTE_SHIFT)
          : data.gradientPoints,
        timestamp: Date.now(),
      };

      const newId = await firebase.pushStroke(pasteData);
      if (newId) {
        // Recompute pathData from the shifted points for local rendering
        let newPathData = pathData;
        if (shiftedPoints && shiftedPoints !== data.points) {
          const inputPoints = unflattenPoints(shiftedPoints);
          newPathData = getStrokePath(inputPoints, data.tool, data.size);
        }
        pastedStrokes.push({ ...data, ...pasteData, id: newId, pathData: newPathData });
      }
    }
    if (pastedStrokes.length > 0) {
      dispatch({ type: 'PASTE_CLIPBOARD', payload: pastedStrokes });
      dispatch({ type: 'SET_TOOL', payload: 'select' });
      dispatch({ type: 'ADD_TOAST', payload: { message: `Pasted ${pastedStrokes.length} item(s) — drag to position` } });
    }
  }, [canvas.clipboard, dispatch, firebase]);

  // --- Clear my strokes ---
  const handleClearMyStrokes = useCallback(async () => {
    const myStrokes = canvas.allStrokes.filter(s => s.userId === user.id);
    if (myStrokes.length === 0) return;
    for (const stroke of myStrokes) {
      await firebase.removeStroke(stroke.id);
    }
    dispatch({ type: 'CLEAR_MY_STROKES', payload: user.id });
    dispatch({ type: 'ADD_TOAST', payload: { message: `Cleared ${myStrokes.length} of your strokes` } });
  }, [canvas.allStrokes, user.id, dispatch, firebase]);

  // --- Recolour selected ---
  // For shapes with a fill, update fillColour too (not just the outline).
  const handleRecolourSelected = useCallback((newColour) => {
    if (canvas.selectedStrokeIds.length === 0) return;
    const updates = canvas.selectedStrokeIds.map(id => {
      const stroke = canvas.allStrokes.find(s => s.id === id);
      const changes = { colour: newColour };
      // If it's a filled shape, also change the fill colour
      if (stroke?.tool === 'shape' && stroke.fillColour) {
        changes.fillColour = newColour;
      }
      return { id, updates: changes };
    });
    dispatch({ type: 'UPDATE_STROKES_BATCH', payload: updates });
    updates.forEach(({ id, updates: changes }) => {
      firebase.updateStrokeInDB(id, changes);
    });
  }, [canvas.selectedStrokeIds, canvas.allStrokes, dispatch, firebase]);

  // --- Transform selected (rotate, scale, flip) ---
  const handleTransformSelected = useCallback((action, value) => {
    if (canvas.selectedStrokeIds.length === 0) return;
    const updates = canvas.selectedStrokeIds.map((id) => {
      const stroke = canvas.allStrokes.find(s => s.id === id);
      if (!stroke) return null;
      let changes = {};
      if (action === 'rotate') changes = { rotation: (stroke.rotation || 0) + value };
      else if (action === 'scale') changes = { scaleX: (stroke.scaleX || 1) * value, scaleY: (stroke.scaleY || 1) * value };
      else if (action === 'flip') changes = { scaleX: -(stroke.scaleX || 1) };
      else if (action === 'flipV') changes = { scaleY: -(stroke.scaleY || 1) };
      return { id, updates: changes };
    }).filter(Boolean);
    if (updates.length > 0) {
      dispatch({ type: 'UPDATE_STROKES_BATCH', payload: updates });
      updates.forEach(({ id, updates: changes }) => firebase.updateStrokeInDB(id, changes));
    }
  }, [canvas.selectedStrokeIds, canvas.allStrokes, dispatch, firebase]);

  // --- Keyboard shortcuts ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const mod = e.metaKey || e.ctrlKey;

      // Undo: Cmd+Z
      if (mod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Redo: Cmd+Shift+Z
      if (mod && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }
      // Copy: Cmd+C
      if (mod && e.key === 'c') {
        if (canvas.selectedStrokeIds.length > 0) {
          e.preventDefault();
          handleCopy();
        }
      }
      // Paste: Cmd+V
      if (mod && e.key === 'v') {
        if (canvas.clipboard.length > 0) {
          e.preventDefault();
          handlePaste();
        }
      }
      // Duplicate: Cmd+D
      if (mod && e.key === 'd') {
        if (canvas.selectedStrokeIds.length > 0) {
          e.preventDefault();
          handleCopy();
          setTimeout(() => handlePaste(), 50);
        }
      }
      // Delete: Del/Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (canvas.selectedStrokeIds.length > 0) {
          e.preventDefault();
          handleDeleteSelected();
        }
      }
      // Select all: Cmd+A
      if (mod && e.key === 'a') {
        e.preventDefault();
        dispatch({ type: 'SET_SELECTED_STROKES', payload: canvas.allStrokes.map(s => s.id) });
        dispatch({ type: 'SET_TOOL', payload: 'select' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo, handleCopy, handlePaste, handleDeleteSelected,
      canvas.selectedStrokeIds, canvas.clipboard, canvas.allStrokes, dispatch]);

  // --- Export as PNG ---
  const handleExport = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const dataUrl = stage.toDataURL({ pixelRatio: 2 });
    const link = document.createElement('a');
    link.download = `drawlab-${room.code}-${Date.now()}.png`;
    link.href = dataUrl;
    link.click();
  }, [room.code]);

  // Host: clear canvas
  const handleClearCanvas = useCallback(async () => {
    await firebase.clearAllStrokes();
    await firebase.pushEvent({ type: 'clear', initiatorId: user.id });
    dispatch({ type: 'CLEAR_ALL_STROKES' });
  }, [firebase, user.id, dispatch]);

  // Host: kick user
  const handleKickUser = useCallback(async (targetUserId) => {
    await firebase.pushEvent({ type: 'kick', initiatorId: user.id, targetUserId });
    await firebase.kickUser(targetUserId);
  }, [firebase, user.id]);

  // Host: lock/unlock room
  const handleToggleLock = useCallback(async () => {
    const newLocked = !room.locked;
    await firebase.updateRoomMeta({ locked: newLocked });
    dispatch({ type: 'ADD_TOAST', payload: { message: newLocked ? 'Room locked' : 'Room unlocked' } });
  }, [room.locked, firebase, dispatch]);

  // Host: set canvas background
  const handleSetBackground = useCallback(async (colour) => {
    await firebase.updateRoomMeta({ background: colour });
    dispatch({ type: 'SET_BACKGROUND', payload: colour });
  }, [firebase, dispatch]);

  // Host: start a game mode
  const handleStartGame = useCallback(async (mode) => {
    // Clear any stale game state from a previous session
    dispatch({ type: 'CLEAR_GAME' });

    // Every game starts with a fresh canvas. Save the current strokes to
    // savedSharedStrokes so we can restore them when the game ends, then
    // clear both local state AND RTDB so every player sees a blank canvas.
    const hadStrokes = (canvas.allStrokes || []).length > 0;
    if (hadStrokes) {
      dispatch({ type: 'SAVE_SHARED_STROKES' });
      try {
        await firebase.clearAllStrokes();
      } catch (err) {
        console.warn('[handleStartGame] Failed to clear strokes:', err);
      }
      dispatch({ type: 'CLEAR_ALL_STROKES' });
    }

    const playerCount = Object.keys(room.members || {}).length;
    await firebase.setGameState({
      mode,
      phase: 'lobby',
      round: 1,
      timerEndsAt: null,
      timerDuration: null,
      hostId: user.id,
      startedAt: Date.now(),
    });
    // Initialise session tracking
    gameSessionRef.current = {
      mode,
      startedAt: Date.now(),
      startPlayerCount: playerCount,
      peakPlayerCount: playerCount,
      completed: false,
    };
    trackEvent('game_started', { mode, player_count: playerCount });

    if (hadStrokes) {
      dispatch({
        type: 'ADD_TOAST',
        payload: { message: 'Canvas cleared for the game — your previous work will come back when the game ends.' },
      });
    }
  }, [firebase, user.id, dispatch, room.members, canvas.allStrokes]);

  // Host: end game and return to free draw
  const handleEndGame = useCallback(async () => {
    const endedMode = game.mode;
    const session = gameSessionRef.current;
    // Clear local state first to stop any hook timers from firing
    dispatch({ type: 'CLEAR_GAME' });
    // Restore shared strokes if we saved them (any game mode that was
    // started via handleStartGame will have savedSharedStrokes set).
    // We restore locally AND write them back to RTDB so every player
    // sees the pre-game canvas come back, not just the host.
    const savedStrokes = canvas.savedSharedStrokes;
    if (savedStrokes && savedStrokes.length > 0) {
      // Clear whatever's in RTDB from the game before restoring
      try {
        await firebase.clearAllStrokes();
      } catch {
        // Non-fatal
      }
      dispatch({ type: 'RESTORE_SHARED_STROKES' });
      // Push the saved strokes back to RTDB in the background so other
      // clients see them via onChildAdded. Don't block the UI.
      firebase.restoreStrokes?.(savedStrokes).catch(() => {});
    } else if (savedStrokes) {
      // Empty array — just clear the savedSharedStrokes flag
      dispatch({ type: 'RESTORE_SHARED_STROKES' });
    }
    try {
      await firebase.clearGame();
    } catch {
      // Ignore errors if game node already removed
    }
    // Clean up Storage images in the background — don't block UI
    if (room.code) {
      clearRoomGameImages(room.code).catch(() => {});
    }
    dispatch({
      type: 'ADD_TOAST',
      payload: {
        message: savedStrokes && savedStrokes.length > 0
          ? 'Game ended — your previous canvas has been restored'
          : 'Game ended — back to free draw',
      },
    });
    if (endedMode) {
      // If game didn't reach completion naturally, it was abandoned
      if (session.mode && !session.completed) {
        const duration = session.startedAt ? Math.round((Date.now() - session.startedAt) / 1000) : 0;
        trackEvent('game_abandoned', {
          mode: endedMode,
          duration_seconds: duration,
          start_player_count: session.startPlayerCount,
          peak_player_count: session.peakPlayerCount,
          end_player_count: Object.keys(room.members || {}).length,
        });
      }
      trackEvent('game_ended', { mode: endedMode });
      gameSessionRef.current = { mode: null, startedAt: null, startPlayerCount: 0, peakPlayerCount: 0, completed: false };
    }
  }, [firebase, dispatch, canvas.savedSharedStrokes, game.mode, room.members, room.code]);

  // Switch to a different room without going via landing page
  const handleSwitchRoom = useCallback(async (code) => {
    try {
      // Check the target room
      const status = await firebase.getRoomStatus(code);
      if (!status.exists) return { error: 'Room not found — it may have expired' };
      if (status.locked) return { error: 'This room is currently locked by the host' };
      if (status.full) return { error: 'This room is full (8/8)' };

      // End any active game
      if (game.mode) {
        dispatch({ type: 'CLEAR_GAME' });
        if (canvas.savedSharedStrokes) dispatch({ type: 'RESTORE_SHARED_STROKES' });
      }

      // Leave current room
      await firebase.leaveRoom();
      dispatch({ type: 'CLEAR_ALL_STROKES' });

      // Join new room
      const colour = assignMemberColour([]);
      await firebase.joinRoom(code, user.id, user.nickname, colour);
      firebase.setupDisconnect(code, user.id);
      firebase.startListeners(code, user.id);

      session.addRoomToHistory({ code, name: status.roomName });
      window.history.pushState(null, '', `/app/room/${code}`);

      dispatch({ type: 'SET_USER', payload: { ...user, colour, isHost: false } });
      dispatch({ type: 'SET_ROOM_CODE', payload: code });
      dispatch({ type: 'ADD_TOAST', payload: { message: `Switched to ${status.roomName || code}` } });

      return {};
    } catch (err) {
      console.error('Error switching room:', err);
      return { error: 'Something went wrong. Please try again.' };
    }
  }, [firebase, dispatch, user, game.mode, canvas.savedSharedStrokes, session]);

  // Leave room
  const handleLeaveRoom = useCallback(async () => {
    await firebase.leaveRoom();
    window.history.pushState(null, '', '/app/');
    dispatch({ type: 'RESET' });
    dispatch({ type: 'SET_SCREEN', payload: 'landing' });
  }, [firebase, dispatch]);

  const focusMode = ui.focusMode;

  return (
    <div className="h-full flex flex-col bg-canvas">
      {!focusMode && <TopBar
        onUndo={handleUndo}
        onRedo={handleRedo}
        onExport={handleExport}
        onToggleGallery={() => setGalleryOpen(!galleryOpen)}
        onToggleReplay={() => setReplayOpen(!replayOpen)}
        onToggleHelp={() => setHelpOpen(!helpOpen)}
        onToggleArtStyles={() => setArtStylesOpen(!artStylesOpen)}
        onLeaveRoom={handleLeaveRoom}
        onSwitchRoom={handleSwitchRoom}
        recentRooms={session.roomHistory}
        onDeleteSelected={handleDeleteSelected}
        onTransformSelected={handleTransformSelected}
        onRecolourSelected={handleRecolourSelected}
        onClearMyStrokes={handleClearMyStrokes}
        canUndo={canvas.localStrokeIds.length > 0}
        canRedo={canvas.redoStack.length > 0}
        hasSelection={canvas.selectedStrokeIds.length > 0}
        galleryOpen={galleryOpen}
        replayOpen={replayOpen}
        helpOpen={helpOpen}
        artStylesOpen={artStylesOpen}
      />}

      <div className="flex-1 flex overflow-hidden">
        {!focusMode && <Toolbar />}

        <CanvasStage
          ref={stageRef}
          onPushStroke={firebase.pushStroke}
          onUpdateStrokeInDB={firebase.updateStrokeInDB}
          onUpdateCursor={firebase.updateCursor}
          disabled={canvasDisabled}
        />

        <MembersPanel />
      </div>

      {ui.hostPanelOpen && user.isHost && (
        <HostPanel
          onClearCanvas={handleClearCanvas}
          onKickUser={handleKickUser}
          onToggleLock={handleToggleLock}
          onSetBackground={handleSetBackground}
          onOpenGameSelector={() => setGameSelectorOpen(true)}
          onOpenGallery={() => setGalleryOpen(true)}
          onEndGame={handleEndGame}
          stageRef={stageRef}
        />
      )}

      {replayOpen && <ReplayPanel stageRef={stageRef} onClose={() => setReplayOpen(false)} />}
      {helpOpen && <HelpPanel onClose={() => setHelpOpen(false)} />}
      {galleryOpen && <GalleryPanel stageRef={stageRef} onClose={() => setGalleryOpen(false)} />}
      {artStylesOpen && <ArtStyleExplorer onClose={() => setArtStylesOpen(false)} />}

      {/* Paywall — listens to usePaywallState() and opens itself */}
      <PaywallModal />

      {/* Focus mode — floating restore button while chrome is hidden */}
      <FocusModePill />

      {/* Floating selection toolbar — shows copy/paste/flip/delete near selection */}
      <SelectionToolbar
        onCopy={handleCopy}
        onPaste={handlePaste}
        onFlip={() => handleTransformSelected('flip', null)}
        onFlipVertical={() => handleTransformSelected('flipV', null)}
        onRotate={(deg) => handleTransformSelected('rotate', deg)}
        onScale={(factor) => handleTransformSelected('scale', factor)}
        onDelete={handleDeleteSelected}
        hasClipboard={canvas.clipboard?.length > 0}
        onClearClipboard={() => dispatch({ type: 'CLEAR_CLIPBOARD' })}
      />

      {/* Game mode selector modal */}
      {gameSelectorOpen && (
        <GameModeSelector
          onSelect={(mode) => {
            setGameSelectorOpen(false);
            handleStartGame(mode);
          }}
          onClose={() => setGameSelectorOpen(false)}
        />
      )}

      {/* Voice chat panel — floats in bottom-right */}
      <VoicePanel />

      {/* Game mode overlay — renders on top of canvas based on game phase */}
      {game.mode && game.phase !== 'off' && (
        <GameRouter stageRef={stageRef} />
      )}

      <ToastStack />
    </div>
  );
}
